import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { getAuthToken } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  PencilIcon,
  ChatBubbleLeftRightIcon,
  DocumentArrowUpIcon,
  UserGroupIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  AcademicCapIcon,
  MapPinIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import AddCommunicationModal from './AddCommunicationModal';
import UpdateStageModal from './UpdateStageModal';
import EditLeadModal from './EditLeadModal';

interface Lead {
  leadId: string;
  branchId: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  studentName: string;
  studentAge: number;
  dateOfBirth?: string;
  studentGender?: string;
  religion?: string;
  fatherName?: string;
  motherName?: string;
  alternatePhone?: string;
  address?: string;
  locality?: string;
  pinCode?: string;
  notes?: string;
  currentSchool?: string;
  preferredGrade: string;
  inquirySource: string;
  stage: string;
  priority: string;
  leadScore: number;
  assignedTo?: string;
  assignedStaffName?: string;
  team_id?: string;
  referral_type?: string;
  lead_points?: number;
  createdAt: string;
  communications?: Communication[];
  documents?: Document[];
}

interface Communication {
  communicationId: string;
  date: string;
  type: string;
  notes: string;
  staffMember: string;
  staffId: string;
}

interface Document {
  documentId: string;
  name: string;
  url: string;
  uploadedAt: string;
  uploadedBy: string;
}

const LeadDetails: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'details' | 'communications' | 'documents'>('details');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);

  useEffect(() => {
    if (leadId) {
      fetchLeadDetails();
    }
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setLead(data.data);
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'hot': return 'badge-hot';
      case 'warm': return 'badge-warm';
      case 'cold': return 'badge-cold';
      default: return 'badge-warm';
    }
  };

  const getTeamColor = (teamId: string) => {
    const colors: { [key: string]: string } = {
      blue: '#2563eb',
      green: '#16a34a',
      red: '#dc2626',
      yellow: '#ca8a04',
      orange: '#ea580c'
    };
    return colors[teamId] || '#6b7280';
  };

  const getReferralLabel = (referralType: string) => {
    const labels: { [key: string]: string } = {
      parent: 'Parent Referral',
      student: 'Student Referral',
      staff: 'Staff Referral',
      community: 'Community Referral'
    };
    return labels[referralType] || 'No Referral';
  };

  const getStatusBadge = (stage: string) => {
    switch (stage) {
      case 'new': return 'status-new';
      case 'contacted': return 'status-contacted';
      case 'qualified': return 'status-contacted';
      case 'tour_scheduled': return 'status-scheduled';
      case 'tour_completed': return 'status-scheduled';
      case 'application_submitted': return 'status-applied';
      case 'admitted': return 'status-admitted';
      case 'enrolled': return 'status-admitted';
      case 'lost': return 'status-rejected';
      default: return 'status-new';
    }
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Lead not found</p>
        <button onClick={() => navigate('/leads')} className="btn-primary mt-4">
          Back to Leads
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Modals */}
      <AddCommunicationModal
        isOpen={showCommunicationModal}
        onClose={() => setShowCommunicationModal(false)}
        leadId={lead.leadId}
        onCommunicationAdded={fetchLeadDetails}
      />

      <UpdateStageModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        leadId={lead.leadId}
        currentStage={lead.stage}
        onStageUpdated={fetchLeadDetails}
      />

      <EditLeadModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        lead={lead}
        onLeadUpdated={fetchLeadDetails}
      />

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{lead.studentName}</h1>
            <span className={getPriorityBadge(lead.priority)}>
              {lead.priority}
            </span>
            <span className={getStatusBadge(lead.stage)}>
              {lead.stage.replace('_', ' ')}
            </span>
          </div>
          <p className="text-gray-600">Parent: {lead.parentName}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowStageModal(true)}
            className="btn-outline flex items-center"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Update Stage
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="btn-outline flex items-center"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Edit
          </button>
          <button
            onClick={() => navigate('/leads')}
            className="btn-outline"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Lead Score</p>
              <p className="text-2xl font-bold text-gray-900">{lead.leadScore}</p>
            </div>
            <div className={`p-3 rounded-lg ${getLeadScoreColor(lead.leadScore)}`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Communications</p>
              <p className="text-2xl font-bold text-gray-900">{lead.communications?.length || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Documents</p>
              <p className="text-2xl font-bold text-gray-900">{lead.documents?.length || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <DocumentArrowUpIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Days Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {lead.createdAt && !isNaN(new Date(lead.createdAt).getTime())
                  ? Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60 * 24))
                  : 0}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('communications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'communications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Communications ({lead.communications?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'documents'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Documents ({lead.documents?.length || 0})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Student Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <AcademicCapIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-sm font-medium text-gray-900">{lead.studentName}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(lead.dateOfBirth)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{lead.studentGender || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-600">Age</p>
                    <p className="text-sm font-medium text-gray-900">{lead.studentAge} years</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-600">Religion</p>
                    <p className="text-sm font-medium text-gray-900">{lead.religion || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-sm text-gray-600">Current School</p>
                  <p className="text-sm font-medium text-gray-900">{lead.currentSchool || 'Not specified'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <AcademicCapIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Preferred Grade</p>
                  <p className="text-sm font-medium text-gray-900">{lead.preferredGrade}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Parent / Guardian Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Parent Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <UserGroupIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Father's Name</p>
                    <p className="text-sm font-medium text-gray-900">{lead.fatherName || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <UserGroupIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Mother's Name</p>
                    <p className="text-sm font-medium text-gray-900">{lead.motherName || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <UserGroupIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Primary Contact</p>
                  <p className="text-sm font-medium text-gray-900">{lead.parentName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-sm font-medium text-gray-900">{lead.parentEmail || 'N/A'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <PhoneIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Primary Phone</p>
                    <p className="text-sm font-medium text-gray-900">{lead.parentPhone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <PhoneIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Alternate Phone</p>
                    <p className="text-sm font-medium text-gray-900">{lead.alternatePhone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Address Details */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Details</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPinIcon className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Residential Address</p>
                  <p className="text-sm font-medium text-gray-900">{lead.address || 'Not specified'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <MapPinIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Locality</p>
                    <p className="text-sm font-medium text-gray-900">{lead.locality || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPinIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">PIN Code</p>
                    <p className="text-sm font-medium text-gray-900">{lead.pinCode || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Inquiry Source</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{lead.inquirySource.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Branch</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{lead.branchId}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Assigned To</span>
                <span className="text-sm font-medium text-gray-900">{lead.assignedStaffName || 'Unassigned'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Team</span>
                <span>
                  {lead.team_id ? (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white"
                      style={{ backgroundColor: getTeamColor(lead.team_id) }}>
                      {lead.team_id.toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">No Team</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Referral Type</span>
                <span className="text-sm font-medium text-gray-900">
                  {lead.referral_type ? getReferralLabel(lead.referral_type) : 'No Referral'}
                </span>
              </div>
              {lead.notes && (
                <div className="py-2 border-b border-gray-100">
                  <p className="text-sm text-gray-600 mb-1">Notes</p>
                  <p className="text-sm font-medium text-gray-900">{lead.notes}</p>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(lead.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="card bg-gradient-to-br from-purple-50 to-blue-50 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Recommendations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Next Best Action</p>
                  <p className="text-sm text-gray-600">Schedule a school tour within 3 days</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Conversion Probability</p>
                  <p className="text-sm text-gray-600">{Math.round(lead.leadScore * 0.8)}% chance of enrollment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'communications' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Communication History</h3>
            <button
              onClick={() => setShowCommunicationModal(true)}
              className="btn-primary flex items-center"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              Add Communication
            </button>
          </div>

          {lead.communications && lead.communications.length > 0 ? (
            <div className="space-y-4">
              {lead.communications.map((comm) => (
                <div key={comm.communicationId} className="card">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900 capitalize">{comm.type}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        by {comm.staffMember}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(comm.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{comm.notes}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No communications yet</p>
              <button
                onClick={() => setShowCommunicationModal(true)}
                className="btn-primary mt-4"
              >
                Add First Communication
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
            <button className="btn-primary flex items-center">
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              Upload Document
            </button>
          </div>

          {lead.documents && lead.documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {lead.documents.map((doc) => (
                <div key={doc.documentId} className="card">
                  <DocumentArrowUpIcon className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <DocumentArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No documents uploaded yet</p>
              <button className="btn-primary mt-4">
                Upload First Document
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadDetails;
